// Active Care Plan Digital View
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { formatDate } from "../../../utils/dateUtils";
import { submitFeedback } from "../../../redux/actions/careplanActions";
import styled from "styled-components";
import { createGlobalStyle } from "styled-components";
import UrgentCare from "./UrgentCare";
import Spacer from "../../common/Spacer";
import {
  LinedRightArrow,
  PublishIcon,
  LanguageIcon,
  LeftNavigationIcon,
} from "../../common/Icons";
import Avatar from "../../common/Avatar";
import FloatingMenu from "../../common/FloatingMenu";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import IconButton from "../../common/Button";
import TextField from "../../common/TextField";
import { useGoogleTranslate } from "../../../utils/googleTranslate";
import ItemThumbnail from "../../common/ItemThumbnail";

import {
  OuterWrapper,
  HeaderWrapper,
  HeaderContainer,
  TextOverlay,
  TextContainer,
  BottomTextContainer,
  BottomTextColumn,
  BodyContainer,
  SectionContainer,
  SectionTitle,
  SectionCard,
  ItemCardWrapper,
  ItemCard,
  ItemContent,
} from "../../common/PrintHelper";

const GoogleMenuStyles = createGlobalStyle`
  #google_translate_element .goog-te-gadget-simple {
    display: flex !important;
    align-items: center;
    padding: 8px 12px !important;
    color: #004F96 !important;
    background: white !important;
    border-radius: 12px !important;
    cursor: pointer !important;
    text-decoration: none !important;
    z-index: 1100 !important;
  }
  #google_translate_element .goog-te-gadget-simple:hover {
    // background: rgba(0,79,150,0.5) !important;
  }
  #google_translate_element .goog-te-gadget-icon {
    display: none !important;
  }
`;

const ActivePlanPublicView = ({ plan }) => {
  useGoogleTranslate();

  const dispatch = useDispatch();

  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Feedback related states
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", feedback: "" });

  const {
    sections = [],
    contacts = [],
    nhsNumber,
    important_info,
    updated_at,
    mdtUsers = [],
    public_id,
  } = plan;

  const handlePrint = () => {
    window.open(`${window.location.origin}/share/${public_id}/print`, "_blank");
  };

  const handleChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // ← call API here
    dispatch(submitFeedback({ publicId: plan.public_id, form }))
      .unwrap()
      .then(() => {
        setFeedbackOpen(false);
        setSuccessOpen(true);
      })
      .catch((err) => {
        console.error("Feedback failed", err);
      });
  };

  const floatingMenuItems = [
    {
      label: "Print View",
      icon: <PublishIcon color="#004F96" />,
      action: handlePrint,
    },
    {
      label: "Select Language",
      // this DIV will bootstrap Google's own dropdown
      icon: <LanguageIcon color="#004F96" />,
      action: () => setLangOpen((open) => !open),
    },
  ];

  if (!nhsNumber) return <div>Loading…</div>;

  return (
    <PageScroller>
      {/* the hidden translator container */}
      {/* <div id="google_translate_element"></div> */}
      {/* — your new language selector — */}
      <OuterWrapper pt={0}>
        {/* HEADER */}
        <HeaderWrapper>
          <HeaderContainer>
            <TextOverlay>
              <TextContainer>
                <img src="/assets/SidebarLogoImage.svg" alt="Sidebar Logo" />
                <BottomTextContainer>
                  <BottomTextColumn>
                    <p className="body-text-small medium secondary-700">
                      NHS number
                    </p>
                    <p className="body-text-regular semi-bold neutral-700">
                      {nhsNumber}
                    </p>
                  </BottomTextColumn>
                  <BottomTextColumn>
                    <p className="body-text-small medium secondary-700">
                      Date published
                    </p>
                    <p className="body-text-regular semi-bold neutral-700">
                      {formatDate(updated_at)}
                    </p>
                  </BottomTextColumn>
                </BottomTextContainer>
              </TextContainer>
            </TextOverlay>
          </HeaderContainer>
        </HeaderWrapper>

        {/* BODY */}
        <BodyContainer>
          {sections
            .filter((section) =>
              section.items.some((item) => item.answer?.trim())
            )
            .map((section) => (
              <SectionContainer key={section.id}>
                <SectionTitle color={section.color}>
                  <h4 className="semi-bold neutral-100">{section.title}</h4>
                </SectionTitle>
                <Spacer height="16px" />

                {section.resource_link && (
                  <FindOutMore
                    className="primary-600"
                    onClick={() => window.open(section.resource_link, "_blank")}
                  >
                    Find out more <LinedRightArrow />
                  </FindOutMore>
                )}

                <Spacer height="32px" />
                <SectionCard>
                  {section.items.map((item) => (
                    <ItemCardWrapper key={item.id}>
                      <ItemCard>
                        <ItemThumbnail
                          src={item?.image_url}
                          alt={item?.image_alt_text || item?.title}
                          width={"150px"}
                          height={"150px"}
                        />

                        <ItemContent>
                          <h6 className="semi-bold">{item.title}</h6>
                          <p>{item.answer || "No answer provided"}</p>

                          {item.resource_link && (
                            <FindOutMore
                              className="primary-600"
                              onClick={() =>
                                window.open(item.resource_link, "_blank")
                              }
                            >
                              View resources <LinedRightArrow />
                            </FindOutMore>
                          )}
                        </ItemContent>
                      </ItemCard>
                    </ItemCardWrapper>
                  ))}
                </SectionCard>
                <Spacer height="16px" />
              </SectionContainer>
            ))}

          {/* URGENT CARE */}
          <ImportantInfoContainer>
            <UrgentCare
              retrievedContacts={contacts}
              mode="view"
              initialImportantInfo={important_info}
            />
          </ImportantInfoContainer>
        </BodyContainer>

        {/* FOOTER */}
        <FooterContainer>
          <ContributersWrapper>
            <Contributers>
              <PeopleContainerWrapper>
                <h6 className="semi-bold">Care plan collaborators</h6>
                <PeopleContainer>
                  {mdtUsers.map((u) => (
                    <UserCard key={u.id}>
                      <Avatar
                        name={u.name}
                        profilePictureUrl={u.profile_picture}
                      />
                      <UserDetails>
                        <p className="body-text-regular semi-bold neutral-900">
                          {u.name}
                        </p>
                        <p className="body-text-small regular neutral-900">
                          {u.roles[0]?.name || "Team member"}
                        </p>
                      </UserDetails>
                    </UserCard>
                  ))}
                </PeopleContainer>
              </PeopleContainerWrapper>
            </Contributers>
          </ContributersWrapper>
          <FeedbackContainer>
            <h5 className="semi-bold neutral-100">
              Do you have any feedback or comments regarding this Care Plan?
            </h5>
            {/* <FeedbackButton onClick={() => setFeedbackOpen(true)}>
              <p className="body-text-regular semi-bold primary-500">
                Submit feedback
              </p>
            </FeedbackButton> */}
            <IconButton
              variant="secondary"
              state="idle"
              type="button"
              onClick={() => setFeedbackOpen(true)}
            >
              Submit feedback
            </IconButton>
          </FeedbackContainer>
        </FooterContainer>
      </OuterWrapper>
      <GoogleMenuStyles />
      <div
        id="google_translate_element"
        style={{ display: langOpen ? "block" : "none" }}
      />
      <Drawer open={menuOpen}>
        <FloatingMenu items={floatingMenuItems} position="bottom-center" />
      </Drawer>
      <Handle
        onClick={() => {
          setMenuOpen((o) => !o);
          setLangOpen(false);
        }}
      >
        <LeftNavigationIcon />
      </Handle>
      <DesktopMenu>
        <FloatingMenu items={floatingMenuItems} position="bottom-center" />
      </DesktopMenu>
      {feedbackOpen && (
        <FullScreenOverlay
          title="Feedback form"
          iconClose
          onClose={() => setFeedbackOpen(false)}
          width="528px"
          height="auto"
          isSlimModal
          zIndex={10000}
          footerButtons={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setFeedbackOpen(false),
            },
            {
              label: "Submit",
              variant: "primary",
              onClick: () =>
                document.getElementById("feedbackForm")?.requestSubmit(),
            },
          ]}
        >
          <form id="feedbackForm" onSubmit={handleSubmit}>
            <Field>
              <label>Name</label>
              <TextField
                type="text"
                placeholder="Enter your name"
                value={form.name}
                onChange={handleChange("name")}
                required
              />
            </Field>
            <Field>
              <label>Email</label>
              <StyledEmail
                type="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={handleChange("email")}
                required
                pattern="[^\s@]+@[^\s@]+\.[^\s@]+$"
              />
            </Field>
            <Field>
              <label>Feedback</label>
              <TextField
                isTextArea
                placeholder="Enter your feedback"
                maxLength={500}
                height={"172px"}
                showCharacterLimit
                value={form.feedback}
                onChange={handleChange("feedback")}
                required
              />
            </Field>
          </form>
        </FullScreenOverlay>
      )}

      {successOpen && (
        <FullScreenOverlay
          isPopup
          popupType="save"
          onClose={() => setSuccessOpen(false)}
          buttons={[
            {
              label: "Close",
              variant: "primary",
              onClick: () => setSuccessOpen(false),
            },
          ]}
        >
          <h3>Feedback submitted</h3>
          <p>
            Your feedback has been sent. We will review this and be in touch if
            necessary. Thank you!
          </p>
        </FullScreenOverlay>
      )}
    </PageScroller>
  );
};

export default ActivePlanPublicView;

const PageScroller = styled.div`
  // height: 100vh; /* fill the viewport */
  padding-bottom: 100px;
  // padding-top: 24px;
  overflow-y: auto; /* scroll its own contents */
  background: var(--Colors-Neutral-100);
`;

/* We'll wrap these 'FindOutMore' in <DigitalOnly> so that they do not print */
const FindOutMore = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ImportantInfoContainer = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #ec2d30;
  background: rgba(236, 45, 48, 0.05);

  @media print {
    page-break-inside: avoid;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const ContributersWrapper = styled.div`
  display: flex;
  padding: 0px 24px 24px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;

  background: var(--Colors-Neutral-200, #f8f9fa);
`;

const Contributers = styled.div`
  display: flex;
  padding: 24px 0px;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const PeopleContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const PeopleContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-self: stretch;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(33.33% - 16px); // 3 per row with gap
  min-width: 200px; // Minimum width for each card

  @media (max-width: 768px) {
    width: calc(50% - 12px); // 2 per row on smaller screens
  }

  @media (max-width: 480px) {
    width: 100%; // 1 per row on mobile
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const FeedbackContainer = styled.div`
  display: flex;
  padding: 40px 0px;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;

  background: var(--Colors-Secondary-600, #2399a1);
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;

  h5 {
    width: 439px;
    text-align: center;

    @media (max-width: 480px) {
      width: auto;
      max-width: 100%;
    }
  }
`;

const Handle = styled.div`
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 32px;
  height: 64px;
  background: #004f96;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;

  @media (min-width: 768px) {
    display: none; /* desktop: hide handle */
  }
`;

const Drawer = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  height: 110px;
  width: 100%;
  background: white;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
  transform: translateX(100%);
  transition: transform 0.25s ease-out;

  ${(props) =>
    props.open &&
    `
      transform: translateX(0);
    `}

  @media(min-width: 768px) {
    display: none; /* hide on desktop—you already have FloatingMenu at top-right */
  }
`;

const DesktopMenu = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const StyledEmail = styled(TextField)`
  && {
    /* now specificity is class×2 (20) vs attribute (11) */
    padding: 12px 16px;
  }
`;
